import React, { useRef, useState, useCallback } from 'react';
import { hapticWarning } from '../../utils/haptics';

interface SwipeAction {
  label: string;
  icon?: React.ReactNode;
  color: string;
  onClick: () => void;
}

interface SwipeableRowProps {
  children: React.ReactNode;
  rightActions?: SwipeAction[];
  className?: string;
}

const ACTION_WIDTH = 80;
const VELOCITY_THRESHOLD = 0.4; // px/ms

/**
 * iOS-style swipeable row with reveal actions.
 * Swipe left to reveal action buttons (e.g. delete).
 */
export function SwipeableRow({ children, rightActions = [], className = '' }: SwipeableRowProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [translateX, setTranslateX] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const startTime = useRef(0);
  const currentX = useRef(0);
  const isHorizontal = useRef<boolean | null>(null);
  const didHaptic = useRef(false);

  const maxSwipe = rightActions.length * ACTION_WIDTH;

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    startX.current = touch.clientX;
    startY.current = touch.clientY;
    startTime.current = Date.now();
    currentX.current = isOpen ? -maxSwipe : 0;
    isDragging.current = false;
    isHorizontal.current = null;
    didHaptic.current = false;
  }, [isOpen, maxSwipe]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    const dx = touch.clientX - startX.current;
    const dy = touch.clientY - startY.current;

    // Determine direction on first significant movement
    if (isHorizontal.current === null) {
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
        isHorizontal.current = Math.abs(dx) > Math.abs(dy);
      }
      return;
    }

    if (!isHorizontal.current) return;

    isDragging.current = true;
    const base = isOpen ? -maxSwipe : 0;
    const raw = base + dx;
    // Clamp: no rightward past 0, rubberband past max
    const clamped = raw > 0
      ? raw * 0.2  // rubber band right
      : raw < -maxSwipe
        ? -maxSwipe + (raw + maxSwipe) * 0.3  // rubber band left past max
        : raw;

    currentX.current = clamped;
    setTranslateX(clamped);

    // Haptic when crossing threshold
    if (!didHaptic.current && clamped < -maxSwipe * 0.5) {
      hapticWarning();
      didHaptic.current = true;
    }
  }, [isOpen, maxSwipe]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;

    const elapsed = Date.now() - startTime.current;
    const velocity = Math.abs(currentX.current - (isOpen ? -maxSwipe : 0)) / Math.max(elapsed, 1);

    // Fast flick or past halfway → open/close
    const shouldOpen = velocity > VELOCITY_THRESHOLD
      ? currentX.current < (isOpen ? -maxSwipe : 0)
      : currentX.current < -maxSwipe / 2;

    if (shouldOpen) {
      setTranslateX(-maxSwipe);
      setIsOpen(true);
    } else {
      setTranslateX(0);
      setIsOpen(false);
    }
  }, [isOpen, maxSwipe]);

  const handleActionClick = useCallback((action: SwipeAction) => {
    action.onClick();
    setTranslateX(0);
    setIsOpen(false);
  }, []);

  // Close when tapping the row while open
  const handleClick = useCallback(() => {
    if (isOpen) {
      setTranslateX(0);
      setIsOpen(false);
    }
  }, [isOpen]);

  if (rightActions.length === 0) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div ref={containerRef} className={`relative overflow-hidden ${className}`}>
      {/* Action buttons behind the content */}
      <div className="absolute inset-y-0 right-0 flex">
        {rightActions.map((action, i) => (
          <button
            key={i}
            onClick={() => handleActionClick(action)}
            className="flex flex-col items-center justify-center text-white text-xs font-medium"
            style={{
              width: ACTION_WIDTH,
              backgroundColor: action.color,
            }}
          >
            {action.icon}
            <span className="mt-0.5">{action.label}</span>
          </button>
        ))}
      </div>

      {/* Swipeable content */}
      <div
        className="relative bg-[var(--bg-surface)]"
        style={{
          transform: `translateX(${translateX}px)`,
          transition: isDragging.current ? 'none' : 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleClick}
      >
        {children}
      </div>
    </div>
  );
}

export default SwipeableRow;
